import { useState, useEffect } from "react";

import ReportService from "../../services/report.service";
import SubsiteService from "../../services/subsite.service";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Container, Row, Col } from "react-bootstrap";
import { useSelector } from "react-redux";
import { getSubsite, isSuperAdmin } from "../../reducers/auth";

const AdminReports = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateType, setDateType] = useState('placed');
  const [groupByState, setGroupByState] = useState(false);
  const [catalogStartDate, setCatalogStartDate] = useState('');
  const [catalogEndDate, setCatalogEndDate] = useState('');
  const [catalogAllDates, setCatalogAllDates] = useState(true);
  const [styleCode, setStyleCode] = useState('');
  const [styleCodeError, setStyleCodeError] = useState('');
  const [downloadingZeroInventory, setDownloadingZeroInventory] = useState(false);
  const [downloadingAllSubsitesZeroInventory, setDownloadingAllSubsitesZeroInventory] = useState(false);
  const [cachedAllSubsitesZeroInventoryInfo, setCachedAllSubsitesZeroInventoryInfo] = useState(null);
  const [downloadingCachedAllSubsitesZeroInventory, setDownloadingCachedAllSubsitesZeroInventory] = useState(false);
  const [igniteStartDate, setIgniteStartDate] = useState('');
  const [igniteEndDate, setIgniteEndDate] = useState('');
  const [downloadingIgniteReport, setDownloadingIgniteReport] = useState(false);
  const [transactionsStartDate, setTransactionsStartDate] = useState('');
  const [transactionsEndDate, setTransactionsEndDate] = useState('');
  const [transactionsAllDates, setTransactionsAllDates] = useState(true);
  const [downloadingTransactionsReport, setDownloadingTransactionsReport] = useState(false);
  const [auditStartDate, setAuditStartDate] = useState('');
  const [auditEndDate, setAuditEndDate] = useState('');
  const [auditAllDates, setAuditAllDates] = useState(true);
  const [downloadingAuditReport, setDownloadingAuditReport] = useState(false);
  const [userAuditStartDate, setUserAuditStartDate] = useState('');
  const [userAuditEndDate, setUserAuditEndDate] = useState('');
  const [userAuditAllDates, setUserAuditAllDates] = useState(true);
  const [downloadingUserAuditReport, setDownloadingUserAuditReport] = useState(false);
  const [allSubsites, setAllSubsites] = useState([]);
  const [selectedSubsiteId, setSelectedSubsiteId] = useState('');

  const currentSubsite = useSelector(getSubsite);
  const superAdmin = useSelector(isSuperAdmin);

  useEffect(() => {
    if (superAdmin) {
      SubsiteService.getAllSubsites().then(res => {
        if (res?.data) {
          setAllSubsites(res.data);
          // Default to the admin's current subsite
          if (currentSubsite?.id) setSelectedSubsiteId(currentSubsite.id);
        }
      });
    } else if (currentSubsite?.id) {
      setSelectedSubsiteId(currentSubsite.id);
    }
  }, [superAdmin, currentSubsite]);

  useEffect(() => {
    ReportService.getCachedAllSubsitesZeroInventoryReportInfo()
      .then(info => setCachedAllSubsitesZeroInventoryInfo(info))
      .catch(() => setCachedAllSubsitesZeroInventoryInfo({ exists: false }));
  }, []);

  const downloadOrdersReport = async () => {
    await ReportService.downloadOrdersReport(selectedSubsiteId);
  }

  const downloadOrdersOverviewReport = async () => {
    await ReportService.downloadOrdersOverviewReport(
      { startDate, endDate, dateType, groupByState },
      selectedSubsiteId
    );
  }

  const downloadCatalogPurchaseReport = async () => {
    const dates = catalogAllDates ? {} : { startDate: catalogStartDate, endDate: catalogEndDate };
    await ReportService.downloadCatalogPurchaseReport(dates, selectedSubsiteId);
  }

  const downloadStyleCatalogReport = async () => {
    if (!styleCode || styleCode.trim() === '') {
      setStyleCodeError('Please enter a style code');
      return;
    }
    setStyleCodeError('');
    try {
      await ReportService.downloadStyleCatalogReport(styleCode.trim(), selectedSubsiteId);
    } catch (error) {
      if (error.response?.status === 404) {
        setStyleCodeError(`Style code "${styleCode}" not found in any subsite catalog`);
      } else if (error.response?.status === 400) {
        setStyleCodeError('Style code is required');
      } else {
        setStyleCodeError('Error generating report. Please try again.');
      }
    }
  }

  const downloadZeroInventoryReport = async () => {
    setDownloadingZeroInventory(true);
    try {
      await ReportService.downloadZeroInventoryReport(selectedSubsiteId);
    } catch (error) {
      console.error("Error downloading zero inventory report:", error);
      alert("Error downloading zero inventory report");
    } finally {
      setDownloadingZeroInventory(false);
    }
  }

  const downloadAllSubsitesZeroInventoryReport = async () => {
    setDownloadingAllSubsitesZeroInventory(true);
    try {
      await ReportService.downloadAllSubsitesZeroInventoryReport();
    } catch (error) {
      console.error("Error downloading all subsites zero inventory report:", error);
      alert("Error downloading all subsites zero inventory report");
    } finally {
      setDownloadingAllSubsitesZeroInventory(false);
    }
  }

  const downloadCachedAllSubsitesZeroInventoryReport = async () => {
    setDownloadingCachedAllSubsitesZeroInventory(true);
    try {
      await ReportService.downloadCachedAllSubsitesZeroInventoryReport();
    } catch (error) {
      console.error("Error downloading cached all subsites zero inventory report:", error);
      const message = error.response?.status === 404
        ? "The cached report has not been generated yet. The report runs daily at 12:00 AM."
        : "Error downloading cached all subsites zero inventory report";
      alert(message);
    } finally {
      setDownloadingCachedAllSubsitesZeroInventory(false);
    }
  }

  const downloadIgniteProductsOrderedReport = async () => {
    if (!igniteStartDate || !igniteEndDate) {
      alert("Please select both start and end dates");
      return;
    }
    setDownloadingIgniteReport(true);
    try {
      await ReportService.downloadIgniteProductsOrderedReport(
        { startDate: igniteStartDate, endDate: igniteEndDate },
        selectedSubsiteId
      );
    } catch (error) {
      console.error("Error downloading Ignite products report:", error);
      alert("Error downloading Ignite products report");
    } finally {
      setDownloadingIgniteReport(false);
    }
  }

  const downloadTransactionsReport = async () => {
    setDownloadingTransactionsReport(true);
    try {
      const dates = transactionsAllDates ? {} : { startDate: transactionsStartDate, endDate: transactionsEndDate };
      await ReportService.downloadTransactionsReport(dates, selectedSubsiteId);
    } catch (error) {
      console.error("Error downloading transactions report:", error);
      alert("Error downloading transactions report");
    } finally {
      setDownloadingTransactionsReport(false);
    }
  }

  const downloadCatalogAuditReport = async () => {
    setDownloadingAuditReport(true);
    try {
      const dates = auditAllDates ? {} : { startDate: auditStartDate, endDate: auditEndDate };
      await ReportService.downloadCatalogAuditReport(dates, selectedSubsiteId);
    } catch (error) {
      console.error("Error downloading catalog audit report:", error);
      alert("Error downloading catalog audit report");
    } finally {
      setDownloadingAuditReport(false);
    }
  }

  const downloadUserAuditReport = async () => {
    setDownloadingUserAuditReport(true);
    try {
      const dates = userAuditAllDates ? {} : { startDate: userAuditStartDate, endDate: userAuditEndDate };
      await ReportService.downloadUserAuditReport(dates, selectedSubsiteId);
    } catch (error) {
      console.error("Error downloading user audit report:", error);
      alert("Error downloading user audit report");
    } finally {
      setDownloadingUserAuditReport(false);
    }
  }

  if(!currentSubsite) {
    return <div>Loading...</div>
  } else
  return (
    <Container style={{marginTop: 20}}>

      {/* Subsite Selector */}
      {superAdmin && allSubsites.length > 0 && (
        <Row style={{ marginBottom: 20, padding: '16px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
          <Col>
            <Form.Group>
              <Form.Label><strong>Subsite for Reports</strong></Form.Label>
              <Form.Select
                value={selectedSubsiteId}
                onChange={(e) => setSelectedSubsiteId(e.target.value)}
                style={{ maxWidth: 400 }}
              >
                <option value="">— Select a subsite —</option>
                {allSubsites.map(s => (
                  <option key={s.id} value={s.id}>{s.subsiteName}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                All reports below will use the selected subsite.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      )}

      <Row style={{marginTop: 20}}>
        <Col>
          <Button onClick={downloadOrdersReport} disabled={!selectedSubsiteId}> Billing Orders Report</Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>Orders By Date</h2>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Date Type</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                id="date-type-ordered"
                label="Ordered Date"
                name="dateType"
                value="placed"
                checked={dateType === 'placed'}
                onChange={(e) => setDateType(e.target.value)}
              />
              <Form.Check
                inline
                type="radio"
                id="date-type-finalized"
                label="Finalized Date"
                name="dateType"
                value="finalized"
                checked={dateType === 'finalized'}
                onChange={(e) => setDateType(e.target.value)}
              />
            </div>
          </Form.Group>
          Start Date: <input onChange={(e) => setStartDate(e.target.value)} type="date" name="start_date" id="start_date" /><br/>
          End Date: <input onChange={(e) => setEndDate(e.target.value)} type="date" name="end_date" id="end_date" /><br/>
          <Form.Check
            type="checkbox"
            id="group-by-state"
            label="Group by State"
            checked={groupByState}
            onChange={(e) => setGroupByState(e.target.checked)}
            style={{marginTop: 10}}
          />
          <br/>
          <Button onClick={downloadOrdersOverviewReport} disabled={!startDate || !endDate}>Generate Report</Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>Items Ordered Report</h2>
          <p>Lists all styles in a subsite's catalog and how many of each style was purchased.</p>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Check
            type="checkbox"
            id="catalog-all-dates"
            label="All Dates"
            checked={catalogAllDates}
            onChange={(e) => setCatalogAllDates(e.target.checked)}
          />
          <br/>
          {!catalogAllDates && (
            <>
              Start Date: <input
                onChange={(e) => setCatalogStartDate(e.target.value)}
                type="date"
                name="catalog_start_date"
                id="catalog_start_date"
                value={catalogStartDate}
              /><br/>
              End Date: <input
                onChange={(e) => setCatalogEndDate(e.target.value)}
                type="date"
                name="catalog_end_date"
                id="catalog_end_date"
                value={catalogEndDate}
              /><br/><br/>
            </>
          )}
          <Button
            onClick={downloadCatalogPurchaseReport}
            disabled={!catalogAllDates && (!catalogStartDate || !catalogEndDate)}
          >
            Generate Catalog Purchase Report
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>Style Catalog Report</h2>
          <p>Enter a style code to see which subsites have this product in their catalog, along with available colors and sizes.</p>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Style Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter style code (e.g., PC54)"
              value={styleCode}
              onChange={(e) => {
                setStyleCode(e.target.value);
                setStyleCodeError('');
              }}
              isInvalid={!!styleCodeError}
            />
            <Form.Control.Feedback type="invalid">
              {styleCodeError}
            </Form.Control.Feedback>
          </Form.Group>
          <Button
            onClick={downloadStyleCatalogReport}
            disabled={!styleCode || styleCode.trim() === ''}
          >
            Generate Style Catalog Report
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>Zero Inventory Report</h2>
          <p>Generate a report showing all SanMar styles in the current subsite's catalog that have zero inventory or are missing from the inventory table.</p>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            onClick={downloadZeroInventoryReport}
            disabled={downloadingZeroInventory}
          >
            {downloadingZeroInventory ? 'Generating Report...' : 'Generate Zero Inventory Report'}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>All Subsites Zero Inventory Report</h2>
          <p>This report runs automatically every day at 12:00 AM. Download the latest cached version using the link below.</p>
        </Col>
      </Row>
      <Row>
        <Col>
          {cachedAllSubsitesZeroInventoryInfo?.exists ? (
            <>
              <div className="mb-2">
                <small className="text-muted">
                  Last generated: {new Date(cachedAllSubsitesZeroInventoryInfo.lastGenerated).toLocaleString()}
                </small>
              </div>
              <Button
                variant="link"
                className="p-0 align-baseline"
                onClick={downloadCachedAllSubsitesZeroInventoryReport}
                disabled={downloadingCachedAllSubsitesZeroInventory}
              >
                {downloadingCachedAllSubsitesZeroInventory ? 'Downloading...' : 'Download Latest All Subsites Zero Inventory Report'}
              </Button>
            </>
          ) : (
            <div className="text-muted">
              The cached report has not been generated yet. It will be created automatically at 12:00 AM, or you can generate it on demand below.
            </div>
          )}
          <div className="mt-3">
            <small className="text-muted d-block mb-1">Need fresh data right now? (this can take several minutes)</small>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={downloadAllSubsitesZeroInventoryReport}
              disabled={downloadingAllSubsitesZeroInventory}
            >
              {downloadingAllSubsitesZeroInventory ? 'Generating Report...' : 'Generate Fresh Report Now'}
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>Ignite Products Ordered Report</h2>
          <p>Generate a report showing all Ignite products ordered within a date range.</p>
        </Col>
      </Row>
      <Row>
        <Col>
          Start Date: <input
            onChange={(e) => setIgniteStartDate(e.target.value)}
            type="date"
            name="ignite_start_date"
            id="ignite_start_date"
            value={igniteStartDate}
          /><br/>
          End Date: <input
            onChange={(e) => setIgniteEndDate(e.target.value)}
            type="date"
            name="ignite_end_date"
            id="ignite_end_date"
            value={igniteEndDate}
          /><br/><br/>
          <Button
            onClick={downloadIgniteProductsOrderedReport}
            disabled={downloadingIgniteReport || !igniteStartDate || !igniteEndDate}
          >
            {downloadingIgniteReport ? 'Generating Report...' : 'Generate Ignite Products Report'}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>User Transactions Report</h2>
          <p>Generate a report showing all user transactions grouped by user. Includes balance credits, debits, and other transaction types.</p>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Check
            type="checkbox"
            id="transactions-all-dates"
            label="All Dates"
            checked={transactionsAllDates}
            onChange={(e) => setTransactionsAllDates(e.target.checked)}
          />
          <br/>
          {!transactionsAllDates && (
            <>
              Start Date: <input
                onChange={(e) => setTransactionsStartDate(e.target.value)}
                type="date"
                name="transactions_start_date"
                id="transactions_start_date"
                value={transactionsStartDate}
              /><br/>
              End Date: <input
                onChange={(e) => setTransactionsEndDate(e.target.value)}
                type="date"
                name="transactions_end_date"
                id="transactions_end_date"
                value={transactionsEndDate}
              /><br/><br/>
            </>
          )}
          <Button
            onClick={downloadTransactionsReport}
            disabled={downloadingTransactionsReport || (!transactionsAllDates && (!transactionsStartDate || !transactionsEndDate))}
          >
            {downloadingTransactionsReport ? 'Generating Report...' : 'Generate User Transactions Report'}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>Catalog Audit Report</h2>
          <p>Generate a report of all catalog changes for this subsite (products added/removed, colors added/removed, print items added/removed).</p>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Check
            type="checkbox"
            id="audit-all-dates"
            label="All Dates"
            checked={auditAllDates}
            onChange={(e) => setAuditAllDates(e.target.checked)}
          />
          <br/>
          {!auditAllDates && (
            <>
              Start Date: <input
                onChange={(e) => setAuditStartDate(e.target.value)}
                type="date"
                name="audit_start_date"
                id="audit_start_date"
                value={auditStartDate}
              /><br/>
              End Date: <input
                onChange={(e) => setAuditEndDate(e.target.value)}
                type="date"
                name="audit_end_date"
                id="audit_end_date"
                value={auditEndDate}
              /><br/><br/>
            </>
          )}
          <Button
            onClick={downloadCatalogAuditReport}
            disabled={!selectedSubsiteId || downloadingAuditReport || (!auditAllDates && (!auditStartDate || !auditEndDate))}
          >
            {downloadingAuditReport ? 'Generating Report...' : 'Generate Catalog Audit Report'}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr/>
        </Col>
      </Row>
      <Row>
        <Col>
          <h2>User Audit Report</h2>
          <p>Generate a report of all user-management actions for this subsite (users created, added, removed, deactivated, reactivated) and who performed them.</p>
        </Col>
      </Row>
      <Row>
        <Col>
          <Form.Check
            type="checkbox"
            id="user-audit-all-dates"
            label="All Dates"
            checked={userAuditAllDates}
            onChange={(e) => setUserAuditAllDates(e.target.checked)}
          />
          <br/>
          {!userAuditAllDates && (
            <>
              Start Date: <input
                onChange={(e) => setUserAuditStartDate(e.target.value)}
                type="date"
                name="user_audit_start_date"
                id="user_audit_start_date"
                value={userAuditStartDate}
              /><br/>
              End Date: <input
                onChange={(e) => setUserAuditEndDate(e.target.value)}
                type="date"
                name="user_audit_end_date"
                id="user_audit_end_date"
                value={userAuditEndDate}
              /><br/><br/>
            </>
          )}
          <Button
            onClick={downloadUserAuditReport}
            disabled={!selectedSubsiteId || downloadingUserAuditReport || (!userAuditAllDates && (!userAuditStartDate || !userAuditEndDate))}
          >
            {downloadingUserAuditReport ? 'Generating Report...' : 'Generate User Audit Report'}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};
export default AdminReports;